import type { Ref } from 'vue'
import type { GraphQLFormattedError } from 'graphql'

import type { GuildPort } from '@/shared/ports/guild'
import { clearPlannerState } from './liveSync'
import type { PlannerStateRefs } from './liveSync'

export interface LiveSubscription {
  unsubscribe(): void
}

export interface LiveAccessRevocationCtx {
  readonly subscription: Ref<LiveSubscription | null>
  readonly liveSyncError: Ref<string | null>
  readonly liveSnapshotGeneration: Ref<number>
  readonly clearSelectedRaid: () => Promise<boolean>
  readonly plannerStateRefs: PlannerStateRefs
  readonly guild: Pick<GuildPort, 'clearActive' | 'loadAvailableGuilds'>
}

export interface LiveAccessRevocationApi {
  readonly handle: () => Promise<void>
}

/** The GraphQL-over-WebSocket `next` frame shape relevant to access loss. */
export interface LiveSyncPayload {
  readonly errors?: ReadonlyArray<GraphQLFormattedError>
}

function isReadonlyArray(value: unknown): value is ReadonlyArray<unknown> {
  return Array.isArray(value)
}

function hasErrorMessage(value: unknown): value is { readonly message: string } {
  return typeof value === 'object'
    && value !== null
    && 'message' in value
    && typeof value.message === 'string'
}

function hasErrorList(value: unknown): value is { readonly errors: ReadonlyArray<unknown> } {
  return typeof value === 'object'
    && value !== null
    && 'errors' in value
    && isReadonlyArray(value.errors)
}

function hasForbiddenExtensionCode(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || !('extensions' in value)) return false
  const extensions = value.extensions
  if (typeof extensions !== 'object' || extensions === null || !('code' in extensions)) return false
  return typeof extensions.code === 'string' && extensions.code.toUpperCase() === 'FORBIDDEN'
}

export function isForbiddenLiveSyncError(cause: unknown): boolean {
  if (isReadonlyArray(cause) && cause.some(isForbiddenLiveSyncError)) return true
  if (hasErrorList(cause) && cause.errors.some(isForbiddenLiveSyncError)) return true
  if (hasForbiddenExtensionCode(cause)) return true
  const message = cause instanceof Error
    ? cause.message
    : typeof cause === 'string'
      ? cause
      : hasErrorMessage(cause)
        ? cause.message
        : ''
  return /\bFORBIDDEN\b/i.test(message)
}

/**
 * A `graphql-ws` authorization rejection can be delivered as a normal
 * `next` payload with an `errors` array instead of Observable's `error`.
 * Handle only explicit authorization failures; transient GraphQL errors keep
 * the current hydrated planner state intact.
 */
export function handleForbiddenLiveSyncPayload(
  payload: LiveSyncPayload,
  liveAccessRevocation: LiveAccessRevocationApi,
): boolean {
  if (!isForbiddenLiveSyncError(payload.errors)) return false
  void liveAccessRevocation.handle()
  return true
}

/**
 * Fail closed after a planner subscription reports that its active guild is
 * no longer authorized. The caller owns router navigation; this helper owns
 * the state and capability transition so it can be exercised without a DOM.
 */
export function useLiveAccessRevocation(
  ctx: LiveAccessRevocationCtx,
): LiveAccessRevocationApi {
  let inFlight = false

  async function handle(): Promise<void> {
    if (inFlight) return
    inFlight = true
    const activeSubscription = ctx.subscription.value
    ctx.subscription.value = null
    try {
      activeSubscription?.unsubscribe()
    } catch {
      // A closed transport cannot prevent the local authorization reset.
    }
    ctx.liveSyncError.value = null
    ctx.liveSnapshotGeneration.value += 1
    void ctx.clearSelectedRaid()
    clearPlannerState(ctx.plannerStateRefs)
    ctx.guild.clearActive()
    try {
      // Refresh the locked-guild list so Onboarding can offer Request access.
      await ctx.guild.loadAvailableGuilds()
    } catch {
      // `clearActive()` already made the local capability fail closed.
    } finally {
      inFlight = false
    }
  }

  return { handle }
}
